import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Popover } from '@material-ui/core';

const ToolbarButton = ({ icon, title, active, disabled, onClick, children }) => (
  <button
    type='button'
    className={`cell-toolbar-btn${active ? ' active' : ''}${disabled ? ' disabled' : ''}`}
    title={title}
    onMouseDown={(e) => {
      e.preventDefault();
      if (!disabled && onClick) onClick(e);
    }}
    disabled={disabled}
  >
    {icon || children}
  </button>
);

const ToolbarSeparator = () => <div className='cell-toolbar-sep' />;

const getCharOffset = (editable, container, offset) => {
  if (!container || container.nodeType !== 3) return null;
  let charCount = 0;
  const walker = document.createTreeWalker(editable, NodeFilter.SHOW_TEXT, null, false);
  let node;
  while ((node = walker.nextNode())) {
    if (node === container) {
      return charCount + offset;
    }
    charCount += node.nodeValue ? node.nodeValue.length : 0;
  }
  return null;
};

const restoreRangeFromOffset = (editable, charOffset) => {
  const range = document.createRange();
  let charCount = 0;
  const walker = document.createTreeWalker(editable, NodeFilter.SHOW_TEXT, null, false);
  let node;
  while ((node = walker.nextNode())) {
    const len = node.nodeValue ? node.nodeValue.length : 0;
    if (charCount + len >= charOffset) {
      range.setStart(node, charOffset - charCount);
      range.collapse(true);
      return range;
    }
    charCount += len;
  }
  range.selectNodeContents(editable);
  range.collapse(false);
  return range;
};

const insertAnchorAtCharOffset = (editable, anchor, charOffset) => {
  if (charOffset === null || charOffset === undefined) {
    editable.appendChild(anchor);
    return;
  }
  let charCount = 0;
  const walker = document.createTreeWalker(editable, NodeFilter.SHOW_TEXT, null, false);
  let node;
  while ((node = walker.nextNode())) {
    const len = node.nodeValue ? node.nodeValue.length : 0;
    if (charCount + len >= charOffset) {
      const splitOffset = charOffset - charCount;
      const parent = node.parentNode;
      if (splitOffset === 0) {
        parent.insertBefore(anchor, node);
      } else if (splitOffset >= len) {
        if (node.nextSibling) {
          parent.insertBefore(anchor, node.nextSibling);
        } else {
          parent.appendChild(anchor);
        }
      } else {
        const after = node.splitText(splitOffset);
        parent.insertBefore(anchor, after);
      }
      return;
    }
    charCount += len;
  }
  editable.appendChild(anchor);
};

const restoreSelectionAndFocus = (range) => {
  if (range) {
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    let node = range.commonAncestorContainer;
    if (node.nodeType === 3) {
      node = node.parentNode;
    }

    const editable = node.closest ? node.closest('[contenteditable="true"]') : null;
    if (editable) {
      if (document.activeElement !== editable) {
        editable.focus();
      }
    }
  }
};

const ToolbarDropdown = ({ value, options, onChange, title, width = '100px', displayValue }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const savedSelection = useRef(null);

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      savedSelection.current = selection.getRangeAt(0).cloneRange();
    }

    setOpen(true);
  };

  const handleSelect = (optionValue) => {
    restoreSelectionAndFocus(savedSelection.current);
    onChange(optionValue);
    setOpen(false);
  };

  const currentLabel = options.find((o) => o.value === value)?.label || displayValue || value;

  return (
    <>
      <button
        ref={anchorRef}
        type='button'
        className='cell-toolbar-select-btn'
        style={{ width }}
        onMouseDown={handleOpen}
        title={title}
      >
        <span className='select-label'>{currentLabel}</span>
        <span className='select-arrow'>▼</span>
      </button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => {
          if (savedSelection.current) {
            restoreSelectionAndFocus(savedSelection.current);
          }
          setOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <div className='cell-toolbar-dropdown-menu'>
          {options.map((option) => (
            <div
              key={option.value}
              className={`dropdown-option${value === option.value ? ' selected' : ''}`}
              style={option.style}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(option.value);
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
};

const ColorPicker = ({ value, onChange, title, disabled }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const savedSelection = useRef(null);

  const colors = [
    '#000000',
    '#333333',
    '#666666',
    '#999999',
    '#cccccc',
    '#ffffff',
    '#ff0000',
    '#ff6600',
    '#ffcc00',
    '#33cc33',
    '#3399ff',
    '#9933ff',
    '#ff3399',
    '#cc6600',
    '#99cc00',
    '#00cccc',
    '#0066cc',
    '#6600cc',
    '#7a6a55',
    '#0b353f',
    '#29e67c',
    '#dc4d40',
    '#f5a623',
    '#4a90d9',
  ];

  const handleOpen = (e) => {
    e.preventDefault();
    if (disabled) return;
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      savedSelection.current = selection.getRangeAt(0).cloneRange();
    }
    setOpen(true);
  };

  const handleColorSelect = (color) => {
    restoreSelectionAndFocus(savedSelection.current);
    onChange(color);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type='button'
        className={`cell-toolbar-color-btn${disabled ? ' disabled' : ''}`}
        title={title}
        onMouseDown={handleOpen}
        disabled={disabled}
      >
        <span className='color-swatch' style={{ backgroundColor: value || '#000000' }} />
        <span className='color-arrow'>▼</span>
      </button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => {
          if (savedSelection.current) {
            restoreSelectionAndFocus(savedSelection.current);
          }
          setOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <div className='cell-toolbar-color-picker'>
          {colors.map((color) => (
            <button
              key={color}
              type='button'
              className={`color-option${value === color ? ' selected' : ''}`}
              style={{ backgroundColor: color }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleColorSelect(color);
              }}
              title={color}
            />
          ))}
        </div>
      </Popover>
    </>
  );
};

const FontSizeSelect = ({ value, onChange }) => {
  const sizes = [
    { label: 'Small', value: '2' },
    { label: 'Normal', value: '3' },
    { label: 'Large', value: '5' },
    { label: 'Huge', value: '7' },
  ];

  const safeValue = value ? String(value) : '3';

  return (
    <ToolbarDropdown
      value={safeValue}
      options={sizes}
      onChange={onChange}
      title='Font Size'
      width='100px'
      displayValue={sizes.find((s) => s.value === safeValue)?.label || 'Normal'}
    />
  );
};

const FontFamilySelect = ({ value, onChange }) => {
  const fonts = [
    { label: 'Arial', value: 'Arial', style: { fontFamily: 'Arial, sans-serif' } },
    {
      label: 'Times New Roman',
      value: 'Times New Roman',
      style: { fontFamily: 'Times New Roman, serif' },
    },
    { label: 'Georgia', value: 'Georgia', style: { fontFamily: 'Georgia, serif' } },
    {
      label: 'Courier New',
      value: 'Courier New',
      style: { fontFamily: 'Courier New, monospace' },
    },
    {
      label: 'Verdana',
      value: 'Verdana',
      style: { fontFamily: 'Verdana, sans-serif' },
    },
  ];

  return (
    <ToolbarDropdown
      value={value}
      options={fonts}
      onChange={onChange}
      title='Font Family'
      width='120px'
    />
  );
};

const Icons = {
  bold: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z' />
    </svg>
  ),
  italic: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z' />
    </svg>
  ),
  underline: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z' />
    </svg>
  ),
  strikethrough: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M10 19h4v-3h-4v3zM5 4v3h5v3h4V7h5V4H5zM3 14h18v-2H3v2z' />
    </svg>
  ),
  alignLeft: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z' />
    </svg>
  ),
  alignCenter: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z' />
    </svg>
  ),
  alignRight: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z' />
    </svg>
  ),
  listOrdered: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z' />
    </svg>
  ),
  listBullet: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z' />
    </svg>
  ),
  link: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z' />
    </svg>
  ),
  image: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z' />
    </svg>
  ),
  undo: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z' />
    </svg>
  ),
  redo: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z' />
    </svg>
  ),
  cell: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M4 4h16v16H4V4zm2 2v12h12V6H6zm2 2h8v8H8V8z' />
    </svg>
  ),
  delete: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path
        d='M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z'
        fill='#DC4016'
      />
    </svg>
  ),
  duplicate: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z' />
    </svg>
  ),
  bringFront: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M3 3h8v8H3V3zm2 2v4h4V5H5zm6-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5z' />
    </svg>
  ),
  sendBack: (
    <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
      <path d='M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z' />
    </svg>
  ),
};

const CellToolbar = ({
  onAddCell,
  onDeleteCell,
  onDuplicateCell,
  onBringToFront,
  onSendToBack,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  selectedCell,
  onUpdateSelectedCellContent,
  isReadOnly = false,
}) => {
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    fontSize: '3',
    fontName: 'Arial',
    foreColor: '#333333',
    backColor: 'transparent',
    justifyLeft: true,
    justifyCenter: false,
    justifyRight: false,
  });

  const pendingStyleRef = useRef({});

  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkError, setLinkError] = useState('');
  const linkInputRef = useRef(null);
  const savedLinkSelection = useRef(null);
  const savedLinkRangeSnapshot = useRef(null);
  const savedLinkCharOffset = useRef(null);
  const savedLinkCharOffsetEnd = useRef(null);
  const savedLinkCollapsed = useRef(true);
  const savedLinkEditable = useRef(null);
  const savedLinkEditableWasEditable = useRef(null);

  const updateFormatState = useCallback(() => {
    try {
      const selection = window.getSelection();
      const isCollapsed = selection?.isCollapsed;

      let rawFont = document.queryCommandValue('fontName') || 'Arial';
      try {
        if (rawFont && typeof rawFont === 'string') {
          rawFont = rawFont
            .replace(/^"(.*)"$/, '$1')
            .split(',')[0]
            .trim();
        }
      } catch (err) {}

      const queriedState = {
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strikeThrough: document.queryCommandState('strikeThrough'),
        fontSize: document.queryCommandValue('fontSize') || '3',
        fontName: rawFont || 'Arial',
        foreColor: document.queryCommandValue('foreColor') || '#333333',
        backColor: document.queryCommandValue('backColor') || 'transparent',
        justifyLeft: document.queryCommandState('justifyLeft'),
        justifyCenter: document.queryCommandState('justifyCenter'),
        justifyRight: document.queryCommandState('justifyRight'),
      };

      if (isCollapsed && Object.keys(pendingStyleRef.current).length > 0) {
        setFormatState((prev) => ({ ...queriedState, ...pendingStyleRef.current }));
      } else {
        if (!isCollapsed) {
          pendingStyleRef.current = {};
        }
        setFormatState(queriedState);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', updateFormatState);
    return () => document.removeEventListener('selectionchange', updateFormatState);
  }, [updateFormatState]);

  const execCommand = (command, value = null) => {
    if (isReadOnly) return;

    const selection = window.getSelection();
    let savedRange = null;
    let isCollapsed = false;
    if (selection.rangeCount > 0) {
      savedRange = selection.getRangeAt(0).cloneRange();
      isCollapsed = selection.isCollapsed;
    }

    if (isCollapsed) {
      let stateKey = null;
      if (command === 'fontSize') stateKey = 'fontSize';
      if (command === 'fontName') stateKey = 'fontName';
      if (command === 'foreColor') stateKey = 'foreColor';
      if (command === 'backColor') stateKey = 'backColor';
      if (command === 'bold') stateKey = 'bold';
      if (command === 'italic') stateKey = 'italic';
      if (command === 'underline') stateKey = 'underline';

      if (stateKey) {
        if (value !== null) {
          pendingStyleRef.current[stateKey] = value;
        } else {
          pendingStyleRef.current[stateKey] = !formatState[stateKey];
        }
      }
    } else {
      pendingStyleRef.current = {};
    }

    document.execCommand(command, false, value);

    if (savedRange && !isCollapsed) {
      selection.removeAllRanges();
      selection.addRange(savedRange);
    }

    if (savedRange && savedRange.commonAncestorContainer) {
      let node = savedRange.commonAncestorContainer;
      if (node.nodeType === 3) node = node.parentNode;
      const editable = node.closest('[contenteditable="true"]');
      if (editable && document.activeElement !== editable) {
        editable.focus();
      }
    }

    updateFormatState();
  };

  const handleLink = (e) => {
    if (isReadOnly) return;
    e.preventDefault();
    e.stopPropagation();

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const r = selection.getRangeAt(0).cloneRange();
      savedLinkSelection.current = r;
      savedLinkRangeSnapshot.current = {
        startContainer: r.startContainer,
        startOffset: r.startOffset,
        endContainer: r.endContainer,
        endOffset: r.endOffset,
        collapsed: r.collapsed,
      };
      try {
        let node = r.commonAncestorContainer;
        if (node && node.nodeType === 3) node = node.parentNode;
        const editable = node && node.closest ? node.closest('[contenteditable="true"]') : null;
        savedLinkEditable.current = editable || null;
        if (editable) {
          savedLinkCharOffset.current = getCharOffset(editable, r.startContainer, r.startOffset);
          savedLinkCollapsed.current = r.collapsed;
          if (!r.collapsed) {
            savedLinkCharOffsetEnd.current = getCharOffset(editable, r.endContainer, r.endOffset);
          } else {
            savedLinkCharOffsetEnd.current = null;
          }
        }
      } catch (err) {
        savedLinkEditable.current = null;
      }
    } else {
      const range = document.createRange();
      const sel = window.getSelection();
      let focusNode = sel ? sel.focusNode : null;
      let focusOffset = sel ? sel.focusOffset : 0;

      if (!focusNode) {
        const active = document.activeElement;
        const editable =
          active && active.isContentEditable
            ? active
            : (active && active.closest ? active.closest('[contenteditable="true"]') : null) ||
              document.querySelector('[contenteditable="true"]');

        if (editable) {
          range.selectNodeContents(editable);
          range.collapse(false);
          savedLinkSelection.current = range;
          savedLinkEditable.current = editable;
          savedLinkEditableWasEditable.current = !!editable.isContentEditable;
          if (!savedLinkEditableWasEditable.current) {
            try {
              editable.setAttribute('contenteditable', 'true');
              editable.dataset.tempEditable = 'true';
            } catch (err) {}
          }
        } else {
          range.setStart(document.body, 0);
          range.collapse(true);
          savedLinkSelection.current = range;
          savedLinkEditable.current = null;
          savedLinkEditableWasEditable.current = null;
        }
      } else {
        try {
          range.setStart(focusNode, typeof focusOffset === 'number' ? focusOffset : 0);
          range.collapse(true);
          savedLinkSelection.current = range;
          const editableCandidate =
            focusNode && focusNode.closest ? focusNode.closest('[contenteditable="true"]') : null;
          savedLinkEditable.current = editableCandidate || null;
        } catch (err) {
          const editableFallback =
            focusNode && focusNode.closest
              ? focusNode.closest('[contenteditable="true"]')
              : document.querySelector('[contenteditable="true"]');
          if (editableFallback) {
            range.selectNodeContents(editableFallback);
            range.collapse(false);
            savedLinkSelection.current = range;
            savedLinkEditable.current = editableFallback;
          } else {
            range.setStart(document.body, 0);
            range.collapse(true);
            savedLinkSelection.current = range;
            savedLinkEditable.current = null;
          }
        }
      }
    }

    setLinkUrl('https://');
    setLinkTitle('');
    setLinkError('');
    setShowLinkInput(true);

    return false;
  };

  const handleLinkSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const url = linkUrl.trim();

    if (!url || url === 'https://') {
      setLinkError('Please enter a URL');
      return;
    }

    if (!url.startsWith('https://')) {
      setLinkError('Invalid URL. Only HTTPS URLs are allowed');
      return;
    }

    try {
      new URL(url);
    } catch {
      setLinkError('Invalid URL format');
      return;
    }

    const scrollY = window.scrollY;

    if (savedLinkSelection.current || savedLinkRangeSnapshot.current || savedLinkCharOffset.current !== null) {
      const selection = window.getSelection();
      let editable = savedLinkEditable.current || null;
      let range = null;

      if (editable && savedLinkCharOffset.current !== null) {
        try {
          range = restoreRangeFromOffset(editable, savedLinkCharOffset.current);
          if (!savedLinkCollapsed.current && savedLinkCharOffsetEnd.current !== null) {
            const endRange = restoreRangeFromOffset(editable, savedLinkCharOffsetEnd.current);
            range.setEnd(endRange.startContainer, endRange.startOffset);
          }
        } catch (err) {
          range = null;
        }
      }

      if (!range) {
        const snapshot = savedLinkRangeSnapshot.current;
        if (snapshot && snapshot.startContainer && snapshot.startContainer.isConnected) {
          try {
            range = document.createRange();
            range.setStart(snapshot.startContainer, snapshot.startOffset);
            if (!snapshot.collapsed) {
              range.setEnd(snapshot.endContainer, snapshot.endOffset);
            } else {
              range.collapse(true);
            }
          } catch (err) {
            range = null;
          }
        }
      }

      if (!range) {
        if (editable) {
          range = document.createRange();
          range.selectNodeContents(editable);
          range.collapse(false);
        } else {
          range = document.createRange();
          range.setStart(document.body, 0);
          range.collapse(true);
        }
      }

      if (!editable && range && range.commonAncestorContainer) {
        let node = range.commonAncestorContainer;
        if (node.nodeType === 3) node = node.parentNode;
        editable = node && node.closest ? node.closest('[contenteditable="true"]') : null;
      }

      const linkText = linkTitle.trim() || url;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.textContent = linkText;

      if (editable) {
        const editableContent = (editable.innerHTML || '').trim();
        const plainText = editableContent.replace(/<[^>]*>?/gm, '').trim();
        const isPlaceholder = !plainText || plainText === 'Double Click to Enter Text...';

        const rangeIsInsideEditable = (() => {
          try {
            if (!range || !range.commonAncestorContainer) return false;
            let node = range.commonAncestorContainer;
            if (node.nodeType === 3) node = node.parentNode;
            return editable.contains(node);
          } catch (err) {
            return false;
          }
        })();

        if (isPlaceholder) {
          editable.innerHTML = '';
          editable.appendChild(anchor);
        } else if (rangeIsInsideEditable) {
          try {
            if (!range.collapsed) range.deleteContents();
            range.insertNode(anchor);
            range.setStartAfter(anchor);
            range.collapse(true);
          } catch (err) {
            if (savedLinkCharOffset.current !== null) {
              insertAnchorAtCharOffset(editable, anchor, savedLinkCharOffset.current);
            } else {
              const snap = savedLinkRangeSnapshot.current;
              if (snap && snap.startContainer && snap.startContainer.isConnected) {
                try {
                  const fallbackRange = document.createRange();
                  fallbackRange.setStart(snap.startContainer, snap.startOffset);
                  fallbackRange.collapse(true);
                  fallbackRange.insertNode(anchor);
                } catch (e2) {
                  editable.appendChild(anchor);
                }
              } else {
                editable.appendChild(anchor);
              }
            }
          }
        } else {
          if (savedLinkCharOffset.current !== null) {
            insertAnchorAtCharOffset(editable, anchor, savedLinkCharOffset.current);
          } else {
            editable.appendChild(anchor);
          }
        }
      }
      if (editable) {
        setTimeout(() => {
          try {
            const inner = editable.innerHTML || '';
            const hrefCheck = `href=\"${url}\"`;
            if (!inner.includes(hrefCheck) && !inner.includes(url)) {
              editable.innerHTML =
                inner +
                `<a href=\"${url}\" target=\"_blank\" rel=\"noopener noreferrer\">${url}</a>`;
            }

            try {
              if (typeof onUpdateSelectedCellContent === 'function' && selectedCell) {
                onUpdateSelectedCellContent(editable.innerHTML);
              }
            } catch (err) {}

            try {
              editable.blur();
            } catch (err) {}

            try {
              if (
                editable.dataset &&
                editable.dataset.tempEditable === 'true' &&
                savedLinkEditableWasEditable.current === false
              ) {
                editable.removeAttribute('contenteditable');
                delete editable.dataset.tempEditable;
              }
            } catch (err) {}

            savedLinkEditableWasEditable.current = null;
          } catch (err) {}

          window.scrollTo(0, scrollY);
          setShowLinkInput(false);
          setLinkUrl('https://');
          setLinkError('');
          savedLinkSelection.current = null;
          savedLinkRangeSnapshot.current = null;
          savedLinkCharOffset.current = null;
          savedLinkCharOffsetEnd.current = null;
          savedLinkCollapsed.current = true;
          savedLinkEditable.current = null;
        }, 0);

        return;
      }

      window.scrollTo(0, scrollY);

      savedLinkSelection.current = null;
      savedLinkRangeSnapshot.current = null;
      savedLinkCharOffset.current = null;
      savedLinkCharOffsetEnd.current = null;
      savedLinkCollapsed.current = true;
      savedLinkEditable.current = null;
    }

    setShowLinkInput(false);
    setLinkUrl('https://');
    setLinkTitle('');
    setLinkError('');
    savedLinkSelection.current = null;
    savedLinkRangeSnapshot.current = null;
    savedLinkCharOffset.current = null;
    savedLinkCharOffsetEnd.current = null;
    savedLinkCollapsed.current = true;
    savedLinkEditable.current = null;
  };

  const handleLinkCancel = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (savedLinkSelection.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedLinkSelection.current);

      const range = savedLinkSelection.current;
      let node = range.commonAncestorContainer;
      if (node.nodeType === 3) {
        node = node.parentNode;
      }
      const editable = node.closest ? node.closest('[contenteditable="true"]') : null;
      if (editable) {
        if (
          editable.dataset &&
          editable.dataset.tempEditable === 'true' &&
          savedLinkEditableWasEditable.current === false
        ) {
          try {
            editable.removeAttribute('contenteditable');
            delete editable.dataset.tempEditable;
          } catch (err) {}
        }

        editable.focus();
      }
    }

    setShowLinkInput(false);
    setLinkUrl('https://');
    setLinkTitle('');
    setLinkError('');
    savedLinkSelection.current = null;
    savedLinkEditable.current = null;
    savedLinkEditableWasEditable.current = null;
  };

  const handleImage = () => {
    if (isReadOnly) return;
    if (onAddCell) {
      onAddCell('image');
    }
  };

  return (
    <div className='cell-toolbar'>
      <div className='cell-toolbar-group'>
        <FontFamilySelect
          value={formatState.fontName}
          onChange={(v) => execCommand('fontName', v)}
        />
        <FontSizeSelect value={formatState.fontSize} onChange={(v) => execCommand('fontSize', v)} />
      </div>

      <ToolbarSeparator />

      <div className='cell-toolbar-group'>
        <ToolbarButton
          icon={Icons.bold}
          title='Bold (Ctrl+B)'
          active={formatState.bold}
          disabled={isReadOnly}
          onClick={() => execCommand('bold')}
        />
        <ToolbarButton
          icon={Icons.italic}
          title='Italic (Ctrl+I)'
          active={formatState.italic}
          disabled={isReadOnly}
          onClick={() => execCommand('italic')}
        />
        <ToolbarButton
          icon={Icons.underline}
          title='Underline (Ctrl+U)'
          active={formatState.underline}
          disabled={isReadOnly}
          onClick={() => execCommand('underline')}
        />
        <ToolbarButton
          icon={Icons.strikethrough}
          title='Strikethrough'
          active={formatState.strikeThrough}
          disabled={isReadOnly}
          onClick={() => execCommand('strikeThrough')}
        />
      </div>

      <ToolbarSeparator />

      <div className='cell-toolbar-group'>
        <ColorPicker
          value={formatState.foreColor}
          onChange={(c) => execCommand('foreColor', c)}
          title='Text Color'
          disabled={isReadOnly || selectedCell?.type === 'button' || selectedCell?.type === 'divider'}
        />
        <ColorPicker
          value={formatState.backColor}
          onChange={(c) => execCommand('backColor', c)}
          title='Background Color'
          disabled={isReadOnly || selectedCell?.type === 'button' || selectedCell?.type === 'divider'}
        />
      </div>

      <ToolbarSeparator />

      <div className='cell-toolbar-group'>
        <ToolbarButton
          icon={Icons.alignLeft}
          title='Align Left'
          active={formatState.justifyLeft}
          disabled={isReadOnly || selectedCell?.type === 'button' || selectedCell?.type === 'divider'}
          onClick={() => execCommand('justifyLeft')}
        />
        <ToolbarButton
          icon={Icons.alignCenter}
          title='Align Center'
          active={formatState.justifyCenter}
          disabled={isReadOnly || selectedCell?.type === 'button' || selectedCell?.type === 'divider'}
          onClick={() => execCommand('justifyCenter')}
        />
        <ToolbarButton
          icon={Icons.alignRight}
          title='Align Right'
          active={formatState.justifyRight}
          disabled={isReadOnly || selectedCell?.type === 'button' || selectedCell?.type === 'divider'}
          onClick={() => execCommand('justifyRight')}
        />
      </div>

      <ToolbarSeparator />

      <div className='cell-toolbar-group'>
        <ToolbarButton
          icon={Icons.listOrdered}
          title='Numbered List'
          disabled={isReadOnly}
          onClick={() => execCommand('insertOrderedList')}
        />
        <ToolbarButton
          icon={Icons.listBullet}
          title='Bullet List'
          disabled={isReadOnly}
          onClick={() => execCommand('insertUnorderedList')}
        />
      </div>

      <ToolbarSeparator />

      <div className='cell-toolbar-group'>
        <ToolbarButton
          icon={Icons.link}
          title='Insert Link'
          disabled={isReadOnly || selectedCell?.type !== 'text'}
          onClick={handleLink}
        />
        <ToolbarButton
          icon={Icons.image}
          title='Insert Image'
          disabled={isReadOnly}
          onClick={handleImage}
        />
      </div>

      {selectedCell && (
        <>
          <ToolbarSeparator />
          <div className='cell-toolbar-group cell-actions'>
            <ToolbarButton
              icon={Icons.duplicate}
              title='Duplicate Cell'
              disabled={isReadOnly}
              onClick={onDuplicateCell}
            />
            {/* <ToolbarButton
              icon={Icons.bringFront}
              title='Bring to Front'
              disabled={isReadOnly}
              onClick={onBringToFront}
            />
            <ToolbarButton
              icon={Icons.sendBack}
              title='Send to Back'
              disabled={isReadOnly}
              onClick={onSendToBack}
            /> */}
            <ToolbarButton
              icon={Icons.delete}
              title='Delete Cell'
              disabled={isReadOnly}
              onClick={onDeleteCell}
            />
          </div>
        </>
      )}

      {showLinkInput && (
        <div
          className='cell-link-input-overlay'
          onClick={handleLinkCancel}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            className='cell-link-input-popup'
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className='cell-link-input-header'>
              <span>Insert Link</span>
              <button
                type='button'
                className='cell-link-close-btn'
                onClick={handleLinkCancel}
                onMouseDown={(e) => e.stopPropagation()}
              >
                ×
              </button>
            </div>
            <div className='cell-link-input-body'>
              <div className='cell-link-input-row'>
                <label className='cell-link-input-label'>Link Title (optional)</label>
                <input
                  type='text'
                  className='cell-link-title-input'
                  placeholder='Enter display text...'
                  value={linkTitle}
                  onChange={(e) => {
                    setLinkTitle(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleLinkSubmit(e);
                    else if (e.key === 'Escape') handleLinkCancel(e);
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>
              <div className='cell-link-input-row'>
                <label className='cell-link-input-label'>URL</label>
                <input
                  ref={linkInputRef}
                  type='text'
                  className={`cell-link-url-input ${linkError ? 'error' : ''}`}
                  placeholder='https://example.com'
                  value={linkUrl}
                  onChange={(e) => {
                    setLinkUrl(e.target.value);
                    setLinkError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleLinkSubmit(e);
                    else if (e.key === 'Escape') handleLinkCancel(e);
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  autoFocus
                />
              </div>
              {linkError && <div className='cell-link-error'>{linkError}</div>}
            </div>
            <div className='cell-link-input-footer'>
              <button
                type='button'
                className='cell-link-cancel-btn'
                onClick={handleLinkCancel}
                onMouseDown={(e) => e.stopPropagation()}
              >
                Cancel
              </button>
              <button
                type='button'
                className='cell-link-save-btn'
                onClick={handleLinkSubmit}
                onMouseDown={(e) => e.stopPropagation()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CellToolbar;
